<template>
  <div class="columns is-multiline is-centered" v-if="isLoaded && chartOptions.treedOption && chartOptions.nonTreedOption">
    <div class="column is-12">
      <h3 class="has-text-centered">{{ $t("profile.ABStatus.habHFAssociation.treedChart.sectionHeader") }} </h3>
    </div>
    <div class="column is-12-tablet is-6-desktop spaced-left">
      <h4 class="has-text-centered">{{ $t("profile.ABStatus.habHFAssociation.treedChart.header", {chartType: 'NonTreed'}) }} </h4>
      <vue-highcharts
        type="chart"
        :options="chartOptions.nonTreedOption"
        :redrawOnUpdate="false"
        :oneToOneUpdate="true"
        :animateOnUpdate="true"
        @rendered="onRender"
        @update="onUpdate"
        @destroy="onDestroy"/>
    </div>
    <div class="column is-12-tablet is-6-desktop spaced-right">
      <h4 class="has-text-centered">{{ $t("profile.ABStatus.habHFAssociation.treedChart.header", {chartType: 'Treed'}) }} </h4>
      <vue-highcharts
        type="chart"
        :options="chartOptions.treedOption"
        :redrawOnUpdate="false"
        :oneToOneUpdate="true"
        :animateOnUpdate="true"
        @rendered="onRender"
        @update="onUpdate"
        @destroy="onDestroy"/>
    </div>
  </div>
  <div class=" no-margin-top no-margin-bottom">
    <figure class="no-margin-bottom no-margin-top">
      <div v-html='$t("profile.ABStatus.habHFAssociation.treedChart.caption")'/>
    </figure>
  </div>
  <div class="narrow-div no-margin-top interpretion">
    <ul class="no-margin-top">
      <li v-html='$t("profile.ABStatus.habHFAssociation.treedChart.autoFigure", {sppName: state?.sppName, moreLess: mode})'/>
    </ul>
    <div v-html="(sppContent?.WebTreeGraphText ||'')"></div>
  </div>
</template>

<script>
/*
  Treed and NonTreed chart for prarie region, show them side by side.
  both charts will have same scales.

*/
import { reactive, watch, ref } from 'vue';
import { getTreeNonTreeSiteData } from '@/services/data/graph-data.js'
import Vue3Highcharts from '@/components/vue3-highcharts/index';
import { useApp } from '@/composables/state/profileState';
import { credits, defaultXAxis, defaultYAxis, axisFontStyle } from '@/config/profile-config';
import { mergeDeep } from '@/lib/common';
export default {
  name: 'prairie-spp-hab-assocation-treed-hf-section',
  components: {
    'vue-highcharts': Vue3Highcharts
  },
  props: ['isSpecies'],
  setup (props) {
    const { state, sppContent } = useApp();
    const isLoaded = ref(0);
    const chartType = ['nonTreed', 'treed'];
    const series = reactive({ nonTreed: {}, treed: {} });
    const chart = reactive({ nonTreed: [], treed: [] });
    const categories = reactive({ nonTreed: [], treed: [] });
    const colors = reactive({ nonTreed: [], treed: [] });
    const chartOptions = reactive({});

    const valueCompare = {};
    const mode = ref();
    let maxY = 0;
    const loadData = async () => {
      if (!state.tsn) {
        return;
      }
      const data = await getTreeNonTreeSiteData(state.tsn, state.year, state.isSpecies); /* state.tsn */
      chartType.forEach(type => {
        chart[type] = data?.title;
        /* for each treed or non-treed value, label entry
          save color, label, subCategory
          save mean (for columns), lower/upper (for error bars)
        */
        const mean = [];
        const topError = [];
        const bottomError = [];
        valueCompare[type] = 0;
        data && data[type] && data[type].forEach(row => {
          /* add category with two levels */
          const matchIndex = categories[type].findIndex(x => x.name === row.subCategory);
          if (matchIndex >= 0) {
            categories[type][matchIndex].categories.push(row.label);
          } else {
          /* place holder for adding space between groups, not add for the frist one */
            // if (categories[type].length > 0) {
            //   categories[type].push({ name: '', categories: [''] });
            //   mean.push(0);
            //   topError.push([0, 0]);
            //   bottomError.push([0, 0]);
            // }
            /* end place holder */
            categories[type].push({ name: row.subCategory, categories: [row.label] });
          }
          mean.push(row.mean);
          topError.push([row.upper, row.mean]); // above main bar, using same colors as the bar
          bottomError.push([row.lower, row.mean]); // overlap with main bar so using white color
          colors[type].push(row.color);
          valueCompare[type] += row.mean;
        });

        series[type] = [
          {
            type: 'column',
            data: mean
          },
          {
            type: 'errorbar',
            whiskerWidth: 0,
            data: topError,
            colorByPoint: true,
            colors: colors[type]
          },
          {
            type: 'errorbar',
            whiskerWidth: 0,
            data: bottomError,
            color: '#ffffff'
          }
        ];
        /* get top error */
        maxY = Math.max(maxY, Math.max(...topError.map(x => x[0])));
      });
      /* tree is more/less than nonTree */
      const diffRatio = (valueCompare[chartType[0]] - valueCompare[chartType[1]]) / (valueCompare[chartType[0]] + valueCompare[chartType[1]])
      mode.value = (Math.abs(diffRatio) < 0.1) ? 'similar' : (diffRatio < 0 ? 'higher' : 'lower');

      isLoaded.value++;
      chartOptions[chartType[0] + 'Option'] = packageChartOptions(chartType[0]);
      chartOptions[chartType[1] + 'Option'] = packageChartOptions(chartType[1]);
    };

    const packageChartOptions = (type, label = '') => {
      return {
        credits,
        chart: {
          type: 'column'
          // marginLeft: 20
        },
        title: {
          text: '' // chart[type].title
        },
        xAxis: mergeDeep(defaultXAxis,
          {

            categories: categories[type],
            labels: {
              groupedOptions: [{
                rotation: 0,
                style: axisFontStyle // set red font for labels in 1st-Level same as Y axis
              }, { // suppose to set second layer group font
                style: axisFontStyle
              }]
            // rotation: -45, // 0-level options aren't changed, use them as always
            },
            breaks: [{
              from: 6.9,
              to: 7.0,
              breakSize: 1
            }]
          }),
        yAxis: mergeDeep(defaultYAxis,
          {
          // min: 0,
            max: maxY,
            title: {
              text: chart[type]?.yLabel
            }
          }),
        plotOptions: {
          column: {
            colorByPoint: true,
            colors: colors[type],
            groupPadding: 0,
            // grouping: true,
            dataLabels: {
              // enabled: true,
              crop: false
              // overflow: 'none',
              // useHTML: true,
              // formatter: function () {
              //   return (this.point.y > 0 ? '+' : '') + this.point.y.toFixed(2) + '%';// + useHFCustomIcon(extenedHfCategory, 15, this.point.name)
              // }
            }
          }
        },
        legend: {
          enabled: false
        },
        tooltip: {
          backgroundColor: '#FCFFC5',
          headerFormat: '',
          pointFormatter: function () { return this.category + ': ' + this.y.toFixed(2) + '%'; }
          // pointFormat: `${point.name}: {point.y}%`
        },
        series: series[type]
      }
    };

    watch(() => state.tsn, async (newVal, oldVal) => {
      if (newVal && newVal !== oldVal) {
        loadData();
      }
    });
    return {
      state,
      sppContent,
      loadData,
      chartOptions,
      isLoaded,
      mode,
    }
  },
  created () {
    this.loadData();
  }
}
</script>

<style>

</style>
